import { useEffect, useMemo, useState } from 'react';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Etiqueta } from '@/components/ui/etiqueta';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { avisoDelFreno } from '@/features/mini-prixia/freno-de-la-demo';
import { formatoCantidad, formatoPrecio } from '@/lib/utils/formato-precio';
import { PorQueEstePrecio } from '@/features/pricing-rules/components/por-que-este-precio';
import { lecturaDelPrecio } from '@/features/pricing-rules/lib/lectura-del-precio';
import { nombreDeLaRegla, tituloDeSalida } from '@/features/pricing-rules/lib/salidas-sin-precio';
import type { ClaveDeSalida } from '@/features/pricing-rules/types/pricing-rules.types';
import { desdeFilaDeLaBandeja } from '../lib/explicacion-del-precio';
import { priceProposalsApi } from '../services/price-proposals-api';
import type { PriceProposalRow, ResultadoPorPropuesta } from '../types/price-proposals.types';

/**
 * La ventana «Decidir» de una propuesta sin precio posible (maqueta del pulido,
 * decisión 4). Arriba, la explicación v2 del producto (la misma de Productos,
 * con el hueco en rojo), sin sus opciones; abajo, «Elegí qué hacer con este
 * precio»: una opción por salida, con lo que ganás y qué regla no cumple, las
 * mismas líneas de la explicación. «Aplicar lo mismo a los parecidos» usa la
 * misma clave en las que tienen las mismas reglas que no se pueden cumplir
 * juntas (decisión 6 del 4A); cada una con su propio precio.
 */

const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));

/**
 * «la parecida» / «las 5 parecidas». `total` es el de `/similares`, que incluye a
 * la propuesta misma (revisión final, B3): las parecidas son las otras.
 */
const lasParecidas = (total: number) =>
  total - 1 === 1 ? 'la parecida' : `las ${formatoCantidad(total - 1)} parecidas`;

/** El toast de un desenlace. Un «listo» que esconde que no se aplicó es peor que un error. */
export function avisoDeDesenlace(
  r: ResultadoPorPropuesta,
  precio: number | null
): { title: string; description?: string; variant?: 'destructive' } {
  switch (r.desenlace) {
    case 'APLICADA':
      return { title: `Precio actualizado${precio != null ? ` a ${fmt(precio)}` : ''}` };
    case 'DESCARTADA':
      return { title: 'Quedó como estaba', description: 'No se movió el precio.' };
    case 'PRECIO_CAMBIO':
      return {
        title: 'El precio cambió desde que se propuso',
        description: r.motivo ?? 'Se vuelve a calcular en la próxima corrida.',
        variant: 'destructive',
      };
    // 4B-datos (contrato, punto 12): igual que PRECIO_CAMBIO.
    case 'COSTO_CAMBIO':
      return {
        title: 'El costo cambió desde que se propuso',
        description: r.motivo ?? 'Se vuelve a calcular en la próxima corrida.',
        variant: 'destructive',
      };
    // Saneamiento del 26-sep (T7): lo que se vio no es lo que se iba a aplicar
    // (la corrida reescribió la propuesta). Igual que PRECIO_CAMBIO; la lista se
    // recarga para que se vea la nueva.
    case 'PROPUESTA_CAMBIO':
      return {
        title: 'La propuesta cambió desde que la viste',
        description: r.motivo ?? 'Mirala de nuevo antes de aprobar.',
        variant: 'destructive',
      };
    // También cuando dos personas aprueban la misma a la vez. Revisión final de
    // rama (F1): el motivo del servidor dice quién y cuándo («Ya la aprobó Juan
    // Pérez el 6 oct, 10:32.»).
    case 'YA_RESUELTA':
      return {
        title: 'Alguien la resolvió antes',
        description: r.motivo ?? undefined,
        variant: 'destructive',
      };
    case 'NO_EXISTE':
      return { title: 'Esta propuesta ya no existe', variant: 'destructive' };
    // Lote A del 2-oct (A1): sin aceptar la pérdida (p. ej. el costo subió
    // mientras se miraba). El motivo trae los números.
    case 'DEBAJO_DEL_COSTO':
      return {
        title: 'Queda debajo del costo',
        description: r.motivo ?? 'Mirala de nuevo antes de aprobar.',
        variant: 'destructive',
      };
    // SALIDA_INVALIDA (precio imposible) y ERROR traen un motivo fijo en
    // castellano, escrito para mostrarse tal cual.
    default:
      return {
        title: 'No se pudo aplicar',
        description: r.motivo ?? 'Probá de nuevo.',
        variant: 'destructive',
      };
  }
}

export function FilaSinPrecio({
  fila,
  onResuelta,
}: {
  fila: PriceProposalRow;
  onResuelta: () => void;
}) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  // Soporte («Entrar como») en una demo sólo mira: no aprueba ni descarta propuestas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const salidas = useMemo(() => fila.salidas ?? [], [fila.salidas]);
  const [elegida, setElegida] = useState<ClaveDeSalida | null>(salidas[0]?.clave ?? null);
  // Lo que hace falta de `/similares` para decir ANTES de confirmar si se
  // recorta (tope 200) o si la búsqueda quedó incompleta (500 más viejas).
  const [parecidas, setParecidas] = useState<{
    total: number;
    recortadas: number;
    incompleto: boolean;
  } | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  // La explicación y las líneas de cada opción salen del mismo recorrido: lo
  // que dice la ventana es lo mismo que dice Productos.
  const recorrido = useMemo(() => desdeFilaDeLaBandeja(fila), [fila]);
  // Las líneas de la explicación y, después de lo que ganás, qué reglas cumple
  // cada salida: «cumple «…»» (auditoría I8; la lista de antes lo decía).
  const lineasDe = useMemo(() => {
    const opciones = lecturaDelPrecio(recorrido, 'HOY').opciones ?? [];
    return new Map(
      opciones.map((o) => {
        const s = salidas.find((x) => x.clave === o.clave);
        const cumple = [...new Set(s?.respeta ?? [])].map(
          (id) => `«${nombreDeLaRegla(fila.rangos, id)}»`
        );
        if (cumple.length === 0) return [o.clave, o.lineas];
        const [primera, ...resto] = o.lineas;
        const union =
          cumple.length > 1
            ? `${cumple.slice(0, -1).join(', ')} y ${cumple[cumple.length - 1]}`
            : cumple[0];
        const linea = { texto: `cumple ${union}`, malo: false };
        return [o.clave, primera ? [primera, linea, ...resto] : [linea]];
      })
    );
  }, [recorrido, salidas, fila.rangos]);

  useEffect(() => {
    let vivo = true;
    if (fila.status !== 'PENDING' || salidas.length === 0) return;
    priceProposalsApi
      .similares(fila.id)
      .then(
        (r) =>
          vivo &&
          setParecidas({ total: r.total, recortadas: r.recortadas, incompleto: !!r.incompleto })
      )
      .catch(() => vivo && setParecidas(null));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fila.id]);

  // El precio cambiado bloquea todo. El costo cambiado (Task 0, ajuste 1) bloquea
  // todo MENOS «no mover» y descartar: no escriben precio, así que el costo no
  // importa (Task 0, ajuste 2) y la propuesta no queda sin salida.
  const base = fila.desactualizada || fila.status !== 'PENDING';
  const bloquea = (clave: string | null) => base || (!!fila.costoCambio && clave !== 'NO_MOVER');
  const bloqueada = bloquea(null);
  const salida = salidas.find((s) => s.clave === elegida) ?? null;

  /**
   * Lote A del 2-oct (A1): cuánto se pierde por unidad con una salida, contra el
   * costo de HOY (el backend lo vuelve a mirar). null si no pierde, si no escribe
   * precio («no mover») o si un backend viejo no trae el costo de hoy.
   */
  const perdidaCon = (s: { clave: string; precio: number }) => {
    const costo = fila.costoHoy;
    if (s.clave === 'NO_MOVER' || costo == null || !(costo > 0)) return null;
    const perdida = Math.round((costo - s.precio) * 100) / 100;
    return perdida > 0.005 ? perdida : null;
  };

  /** Debajo del costo, pregunta primero y aplica aceptando la pérdida; si no, aplica. */
  function aplicarEste() {
    if (!salida) return;
    const perdida = perdidaCon(salida);
    if (perdida == null) {
      void aprobarEste(false);
      return;
    }
    confirm({
      title: `¿Aplicar ${fmt(salida.precio)} a «${fila.productName}»?`,
      description: `Queda debajo del costo (${fmt(fila.costoHoy)}): perdés ${fmt(perdida)} por unidad. Queda registrado quién lo aprobó y cuándo.`,
      confirmText: 'Sí, aplicar con pérdida',
      variant: 'destructive',
      onConfirm: () => aprobarEste(true),
    });
  }

  async function aprobarEste(aceptaPerdida: boolean) {
    if (!salida) return;
    setTrabajando(true);
    try {
      // Saneamiento del 26-sep (T7): se manda el precio que mostraba la salida
      // elegida; si la propuesta cambió desde entonces, no se aplica.
      const r = await priceProposalsApi.aprobar(
        fila.id,
        salida.clave,
        salida.precio,
        aceptaPerdida || undefined
      );
      toast(avisoDeDesenlace(r, salida.clave === 'NO_MOVER' ? null : salida.precio));
      onResuelta();
    } catch (e) {
      // Reglas de la demo (343): el mismo aviso del freno que en la bandeja, con «Ver el plan completo».
      const delFreno = avisoDelFreno(e);
      toast(
        delFreno ?? {
          title: 'No se pudo aplicar',
          description: mensajeDeError(e, 'Probá de nuevo.'),
          variant: 'destructive',
        }
      );
    } finally {
      setTrabajando(false);
    }
  }

  function aplicarParecidas() {
    if (!salida || parecidas == null) return;
    confirm({
      title: `¿«${tituloDeSalida(salida.clave, fila.rangos)}» en esta y en ${lasParecidas(parecidas.total)}?`,
      description:
        'Cada uno con su propio precio, calculado con sus reglas. Si a alguno le cambió el precio o el costo desde que se propuso, ése no se toca y te lo decimos.' +
        // `approve-similares` no acepta el precio visto (saneamiento, T7).
        ' Se aplica con el precio que tenga cada una en ese momento.' +
        // Lote A del 2-oct (A1): los parecidos son un lote, también esta misma (auditoría I2).
        ' Los que queden debajo del costo o se hayan calculado con datos viejos no se aplican: se deciden una por una.' +
        (parecidas.recortadas > 0
          ? ` Se aplican los primeros 200; quedan ${parecidas.recortadas} para otra vuelta.`
          : '') +
        (parecidas.incompleto
          ? ' Puede haber más parecidos: se revisaron los 500 más viejos.'
          : ''),
      confirmText: 'Sí, aplicar a todos',
      onConfirm: async () => {
        try {
          const r = await priceProposalsApi.aprobarSimilares(fila.id, salida.clave);
          const bien = (r.porDesenlace.APLICADA ?? 0) + (r.porDesenlace.DESCARTADA ?? 0);
          const mal = r.total - bien;
          toast({
            title: `${bien} de ${r.total} resueltas`,
            description:
              (mal > 0
                ? `${mal} no se pudieron: ${r.resultados
                    .filter((x) => x.motivo)
                    .slice(0, 3)
                    .map((x) => x.motivo)
                    .join(' · ')}`
                : '') +
              (r.recortadas > 0
                ? ` Quedaron ${r.recortadas} afuera por el límite de este envío.`
                : '') +
              (r.incompleto ? ' Puede haber más parecidos: se revisaron los 500 más viejos.' : ''),
            variant: mal > 0 ? 'destructive' : undefined,
          });
          onResuelta();
        } catch (e) {
          toast({
            title: 'No se pudo aplicar',
            description: mensajeDeError(e, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });
  }

  async function descartar() {
    setTrabajando(true);
    try {
      toast(
        avisoDeDesenlace(
          await priceProposalsApi.descartar(fila.id, undefined, fila.priceAfter),
          null
        )
      );
      onResuelta();
    } catch (e) {
      toast({
        title: 'No se pudo descartar',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Etiqueta tono="bloqueo">Conflicto entre reglas</Etiqueta>
        <p className="text-sm text-muted-foreground">
          {/* El costo es el congelado al proponer; si hoy es otro, lo dice el aviso de abajo. */}
          <span className="font-medium text-foreground">{fila.productName}</span> · costo{' '}
          {fmt(fila.costo)} · hoy {fmt(fila.precioActual)} · el más barato{' '}
          {fmt(fila.precioMasBarato)}
        </p>
      </div>

      {/* La explicación v2, la misma de Productos; las opciones van abajo, para elegir. */}
      <PorQueEstePrecio recorrido={recorrido} tiempo="HOY" sinOpciones />

      {fila.desactualizada && (
        <Aviso tono="alerta" className="text-sm">
          El precio cambió desde que se propuso: hay que volver a calcularlo. Se recalcula en la
          próxima corrida.
        </Aviso>
      )}
      {/* Task 0, ajuste 1: el costo de hoy no es el que se usó al proponer. */}
      {fila.costoCambio && (
        <Aviso tono="alerta" className="text-sm">
          El costo cambió desde que se propuso: se vuelve a calcular en la próxima corrida. Mientras
          tanto sólo se puede dejar el precio como está.
        </Aviso>
      )}

      {salidas.length === 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Esta propuesta es de antes: no trae salidas calculadas. Se puede descartar.
          </p>
          <Button
            variant="outline"
            disabled={base || trabajando}
            onClick={() => void descartar()}
            {...sinCambios}
          >
            Descartar
          </Button>
        </div>
      ) : (
        <>
          <div className="text-sm font-semibold">Elegí qué hacer con este precio</div>
          <div role="radiogroup" aria-label="Elegí qué hacer con este precio" className="space-y-2">
            {salidas.map((s) => {
              const titulo = tituloDeSalida(s.clave, fila.rangos);
              return (
                <label
                  key={s.clave}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border bg-card p-3 text-sm',
                    elegida === s.clave && 'border-primary bg-primary/10',
                    bloquea(s.clave) && 'cursor-not-allowed opacity-60'
                  )}
                >
                  <input
                    type="radio"
                    className="mt-0.5 accent-primary"
                    name={`salida-${fila.id}`}
                    aria-label={`${titulo} · ${fmt(s.precio)}`}
                    checked={elegida === s.clave}
                    disabled={bloquea(s.clave)}
                    onChange={() => setElegida(s.clave)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold">{titulo}</span>
                    <ul className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
                      {(lineasDe.get(s.clave) ?? []).map((l, i) => (
                        <li
                          key={`${i}-${l.texto}`}
                          className={cn(l.malo && 'font-semibold text-bloqueo')}
                        >
                          {l.texto}
                        </li>
                      ))}
                    </ul>
                  </span>
                  <span className="whitespace-nowrap text-base font-semibold tabular-nums">
                    {fmt(s.precio)}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={bloquea(salida?.clave ?? null) || !salida || trabajando}
              onClick={() => aplicarEste()}
              {...sinCambios}
            >
              Aplicar a este
            </Button>
            {/* `/similares` cuenta también a esta propuesta: sin otra, no hay «parecidas». */}
            {parecidas != null && parecidas.total > 1 && (
              <Button
                variant="outline"
                disabled={bloqueada || !salida || trabajando}
                onClick={aplicarParecidas}
                {...sinCambios}
              >
                Aplicar lo mismo a esta y a {lasParecidas(parecidas.total)}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground/80">¿Por qué le toca a una persona?</strong> Porque
            cualquier salida deja sin cumplir una regla que pusiste. Cuando existe un precio que las
            cumple todas, el sistema lo elige solo.
          </p>
        </>
      )}
      <ConfirmDialog />
    </div>
  );
}
